// Gallery.jsx (Pure JavaScript + JSX)
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart,
  Sparkles,
  Pause,
  Music,
  Gift,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
  Camera,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

import img1 from "../assets/1.jpg";
import img2 from "../assets/2.jpg";
import img3 from "../assets/3.jpg";
import img4 from "../assets/4.jpg";
import img5 from "../assets/5.jpg";
import img6 from "../assets/6.jpg";
import img7 from "../assets/7.jpg"

const images = [
  {
    src: img1,
  },
  {
    src: img2,
  },
  {
    src: img3,
  },
  {
    src: img4,
  },
  {
    src: img5,
  },
  {
    src: img6,
  },
  {
    src: img7
  }
];

const Gallery = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loadedImages, setLoadedImages] = useState(
    new Array(images.length).fill(false)
  );
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiTimeout = useRef(null);
  const [parallaxY, setParallaxY] = useState(0);

  // Preload images
  useEffect(() => {
    images.forEach((img, i) => {
      const imgEl = new Image();
      imgEl.src = img.src;
      imgEl.onload = () => {
        setLoadedImages((prev) => {
          const copy = [...prev];
          copy[i] = true;
          return copy;
        });
      };
    });
  }, []);

  // Parallax
  useEffect(() => {
    const handleScroll = () => setParallaxY(window.scrollY * 0.4);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Floating hearts
  const [hearts] = useState(() =>
    Array.from({ length: 55 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 30 + 20,
      duration: Math.random() * 12 + 8,
      delay: Math.random() * 5,
    }))
  );

  // Confetti
  const triggerConfetti = () => {
    setShowConfetti(true);
    if (confettiTimeout.current) clearTimeout(confettiTimeout.current);
    confettiTimeout.current = setTimeout(() => setShowConfetti(false), 5000);
  };

  useEffect(() => {
    triggerConfetti();
    return () => {
      if (confettiTimeout.current) clearTimeout(confettiTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (isPlaying) triggerConfetti();
  }, [isPlaying]);

  const openModal = (i) => setSelectedIndex(i);
  const closeModal = () => setSelectedIndex(null);
  const navigateImage = (dir) => {
    if (selectedIndex === null) return;
    const len = images.length;
    const next =
      dir === "next"
        ? (selectedIndex + 1) % len
        : (selectedIndex - 1 + len) % len;
    setSelectedIndex(next);
  };

  return (
    <>
      {/* Floating Hearts */}
      {hearts.map((h) => (
        <motion.div
          key={h.id}
          className="fixed pointer-events-none z-0"
          style={{ left: `${h.x}%`, top: `${h.y}%` }}
          animate={{
            y: [-200, window.innerHeight + 200],
            x: [0, 30, -30, 0],
            opacity: [0, 0.9, 0.9, 0],
            rotate: [0, 360],
          }}
          transition={{
            duration: h.duration,
            repeat: Infinity,
            delay: h.delay,
            ease: "easeInOut",
          }}
        >
          <Heart size={h.size} className="text-rose-300" fill="currentColor" />
        </motion.div>
      ))}

      {/* Confetti */}
      <AnimatePresence>
        {showConfetti &&
          Array.from({ length: 65 }).map((_, i) => (
            <motion.div
              key={`confetti-${i}`}
              className="fixed pointer-events-none z-50"
              style={{ left: `${Math.random() * 100}%`, top: "-20px" }}
              initial={{ y: 0, rotate: 0, opacity: 1 }}
              animate={{
                y: window.innerHeight + 100,
                rotate: Math.random() * 720,
                opacity: [1, 1, 0],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: Math.random() * 3 + 2.5,
                ease: "easeOut",
                delay: Math.random() * 0.6,
              }}
            >
              <Sparkles
                size={Math.random() * 22 + 12}
                className="text-rose-500"
                fill="currentColor"
              />
            </motion.div>
          ))}
      </AnimatePresence>

      {/* Music Toggle */}
      <motion.button
        whileHover={{ scale: 1.12, rotate: 8 }}
        whileTap={{ scale: 0.92 }}
        onClick={() => setIsPlaying(!isPlaying)}
        className="fixed top-6 right-6 z-40 bg-white/85 backdrop-blur-md p-4 rounded-full shadow-2xl border border-rose-200"
      >
        <motion.div
          animate={isPlaying ? { rotate: 360 } : { rotate: 0 }}
          transition={{ duration: 2, repeat: isPlaying ? Infinity : 0, ease: "linear" }}
        >
          {isPlaying ? (
            <Pause className="text-rose-600" size={26} />
          ) : (
            <Music className="text-rose-600" size={26} />
          )}
        </motion.div>
      </motion.button>

      {/* Main Content */}
      <motion.section
        className="py-24 px-6 relative z-10 min-h-screen"
        style={{ y: parallaxY }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, type: "spring", stiffness: 110 }}
          className="text-center mb-24"
        >
          <motion.h1
            className="text-6xl md:text-8xl font-dancing bg-gradient-to-r from-rose-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4 bg-[length:200%_200%]"
            animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
          >
            Our Love Story
          </motion.h1>
          <motion.p
            className="text-2xl text-gray-700 max-w-3xl mx-auto font-light mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Every moment with you is a treasure worth more than gold
          </motion.p>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 120 }}
            className="mt-10 inline-flex items-center gap-4 bg-gradient-to-r from-rose-100 to-purple-100 px-9 py-5 rounded-full shadow-xl"
          >
            <Gift className="text-rose-600" size={30} />
            <div className="flex items-center gap-2">
              <Calendar className="text-rose-600" size={26} />
              <span className="text-rose-700 font-bold text-xl">Forever</span>
            </div>
            <Heart className="text-purple-600" size={30} fill="currentColor" />
          </motion.div>
        </motion.div>

        {/* Image Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto mb-28">
          {images.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 90, scale: 0.88 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: i * 0.13, duration: 0.65, type: "spring" }}
              whileHover={{
                scale: 1.06,
                y: -18,
                transition: { type: "spring", stiffness: 380, damping: 22 },
              }}
              onClick={() => openModal(i)}
              className="relative group cursor-zoom-in overflow-hidden rounded-3xl shadow-2xl"
            >
              {/* Skeleton */}
              {!loadedImages[i] && (
                <div className="absolute inset-0 bg-gradient-to-r from-rose-200 via-purple-200 to-indigo-200 animate-pulse" />
              )}
              {/* Image */}
              <motion.img
                src={img.src}
                alt={img.title}
                className="w-full h-96 object-cover transition-transform duration-700"
                style={{ display: loadedImages[i] ? "block" : "none" }}
                whileHover={{ scale: 1.18 }}
              />
              {/* Anniversary Badge */}
              <motion.div
                initial={{ scale: 0 }}
                whileHover={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="absolute top-4 right-4 bg-gradient-to-r from-rose-500 to-purple-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg"
              >
                {img.anniversary}
              </motion.div>
              {/* Overlay */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-6"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
              >
                <motion.h3
                  className="text-white text-2xl font-dancing mb-1"
                  initial={{ y: 25, opacity: 0 }}
                  whileHover={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 }}
                >
                  {img.title}
                </motion.h3>
                <motion.p
                  className="text-white/90 text-sm font-light"
                  initial={{ y: 25, opacity: 0 }}
                  whileHover={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.13 }}
                >
                  {img.description}
                </motion.p>
                <motion.div
                  className="mt-2 flex items-center gap-2 text-white/80 text-sm"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ delay: 0.18 }}
                >
                  <Clock size={15} />
                  <span>{img.date}</span>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 120 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, type: "spring", stiffness: 100 }}
          className="flex justify-center mt-32"
        >
          <Link
            to="/message"
            className="group relative flex items-center gap-4 bg-gradient-to-r from-rose-600 via-purple-600 to-indigo-600 text-white px-14 py-7 rounded-full font-semibold text-xl shadow-2xl overflow-hidden"
          >
            <motion.span
              className="absolute inset-0 bg-white/15"
              initial={{ x: "-100%" }}
              whileHover={{ x: "0%" }}
              transition={{ duration: 0.65, ease: "easeOut" }}
            />
            <span className="relative z-10 flex items-center gap-3">
              <Camera size={26} />
              Next
            </span>
            <motion.span
              className="relative z-10"
              animate={{ x: [0, 6, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" />
            </motion.span>
          </Link>
        </motion.div>
      </motion.section>

      {/* Modal */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/96 z-50 flex items-center justify-center p-4 backdrop-blur-xl"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.68, rotateY: 90 }}
              animate={{ scale: 1, rotateY: 0 }}
              exit={{ scale: 0.68, rotateY: -90 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Prev */}
              <motion.button
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.92 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage("prev");
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/25 backdrop-blur-sm text-white p-4 rounded-full hover:bg-white/35 transition z-20 shadow-2xl"
              >
                <ChevronLeft size={30} />
              </motion.button>

              {/* Image (Polaroid Style) */}
              <div className="bg-white p-6 rounded-3xl shadow-2xl">
                <img
                  src={images[selectedIndex].src}
                  alt={images[selectedIndex].title}
                  className="w-full h-auto max-h-[75vh] object-contain rounded-2xl"
                />
              </div>

              {/* Next */}
              <motion.button
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.92 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage("next");
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/25 backdrop-blur-sm text-white p-4 rounded-full hover:bg-white/35 transition z-20 shadow-2xl"
              >
                <ChevronRight size={30} />
              </motion.button>


              {/* Info */}
              <motion.div
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.22, type: "spring", stiffness: 120 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/65 backdrop-blur-sm text-white px-9 py-5 rounded-full text-center"
              >
                <p className="text-lg font-medium">{images[selectedIndex].title}</p>
                <p className="text-sm opacity-90">{images[selectedIndex].description}</p>
                <p className="text-xs opacity-75 mt-1">{images[selectedIndex].date}</p>
                <div className="flex items-center gap-2 mt-3 justify-center">
                  {images.map((_, i) => (
                    <motion.div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === selectedIndex ? "bg-white" : "bg-white/35"
                      }`}
                      animate={i === selectedIndex ? { scale: [1, 1.6, 1] } : {}}
                      transition={{ duration: 0.45 }}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;